import React from "react";
import { Search, Plus, Pencil, ExternalLink, Link2 } from "lucide-react";
import { Button, Input, Spinner } from "@/components/ui";
import { SmartLink } from "@/services/bedrock/dtos";
import { useSmartLinkSearch } from "@/utils";

interface SmartLinkListViewProps {
  searchQuery: string;
  onSearchChange: (val: string) => void;
  debouncedSearch: string;
  onSelect: (link: SmartLink) => void;
  onEdit: (link: SmartLink) => void;
  onCreateNew: () => void;
}

export const SmartLinkListView: React.FC<SmartLinkListViewProps> = ({
  searchQuery,
  onSearchChange,
  debouncedSearch,
  onSelect,
  onEdit,
  onCreateNew,
}) => {
  const { data, isLoading } = useSmartLinkSearch({
    searchTerm: debouncedSearch,
    isActive: true,
  });

  const links = data?.content || [];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 text-muted-foreground">
        <Spinner className="size-8" />
        <p>Loading smart links...</p>
      </div>
    );
  }

  if (links.length === 0 && !debouncedSearch) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-4">
        <div className="bg-slate-100 p-4 rounded-full">
          <Link2 className="size-8 text-slate-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">No Smart Links yet</h3>
          <p className="text-sm text-muted-foreground max-w-[280px]">
            Create your first smart link to track clicks and conversions from your social posts.
          </p>
        </div>
        <Button onClick={onCreateNew}>
          <Plus className="size-4 mr-2" />
          Create Smart Link
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search smart links..."
          className="pl-9"
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
        />
      </div>

      <div className="max-h-[400px] overflow-y-auto flex flex-col gap-2 pr-1">
        {links.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm border border-dashed rounded-lg">
            No matches found for "{debouncedSearch}"
          </div>
        ) : (
          links.map(link => (
            <div
              key={link.id}
              className="group flex items-center justify-between p-3 border rounded-lg hover:border-primary hover:bg-slate-50 transition-colors cursor-pointer"
              onClick={() => onSelect(link)}
            >
              <div className="flex flex-col min-w-0 mr-4">
                <span className="font-medium text-foreground">{link.name}</span>
                <span className="text-xs text-muted-foreground">{link.destinationUrl}</span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onEdit(link);
                  }}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button variant="ghost" size="icon" className="size-8" type="button" onClick={e => e.stopPropagation()}>
                  <a href={link.destinationUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-4" />
                  </a>
                </Button>
                <div className="ml-2 bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                  Select
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Button variant="outline" type="button" className="w-full" onClick={onCreateNew}>
        <Plus className="size-4 mr-2" />
        Create New Smart Link
      </Button>
    </div>
  );
};
